import {NotFoundError} from '@packages/utils';
import type {GetServerSideProps, GetServerSidePropsResult} from 'next';
import Head from 'next/head';
import {useRouter} from 'next/router';
import * as z from 'zod';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import type {PostSchema} from '../../../core/entities/columns/schema';
import type {GetRecruitmentListResponse} from '../../../core/entities/recruitment/schema';
import {getCompanyPagePost} from '../../../core/repositories/columnRepository';
import {
  type GetCompanyInfoResponse,
  getCompanyInfo,
} from '../../../core/repositories/companiesRepository';
import {
  type GetCompanyDetailResponse,
  type ResultGetCompanyDetail,
  getCompanyDetail,
} from '../../../core/repositories/companyDetailRepository';
import {getRecruitmentList} from '../../../core/repositories/recruitmentRepository';
import {
  type GetUserDataForAnalyticsResponseType,
  getUserDataForAnalytics,
} from '../../../core/repositories/usersMeRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {ArchivePage} from '../../../features/columns/components/ArchivePage';
import {ColumnLayout} from '../../../features/columns/components/Layout';
import {handleParsedContent} from '../../../features/columns/usecases/handleParsedContent';
import {
  PostPageType,
  handlePostRedirectToPreviewPage,
} from '../../../features/columns/usecases/handlePostRedirectToPreviewPage';
import {CompanyDetailPage} from '../../../features/company/components/CompanyDetailPage';
import {withHandler} from '../../../server/token/withHandler';
import {ScriptForGA} from '../../../shared/components/ScriptForGA';
import {NoLeftSidebarLayout} from '../../../shared/components/layouts/NoLeftSidebarLayout';
import {createArticleDetailStructuredData} from '../../../shared/services/createArticleDetailStructuredDate';
import {UnexpectedError} from '../../../utils/unexpectedError';
import type {NextPageWithLayout} from '../../_app';

type Props = {
  userDataForAnalytics?: GetUserDataForAnalyticsResponseType;
  post?: PostSchema;
  parsedContent?: string;
  isWpContent?: boolean;
  companyInfo?: GetCompanyInfoResponse;
  recruitmentList?: GetRecruitmentListResponse;
  companyDetailResponse?: GetCompanyDetailResponse;
  company?: GetCompanyInfoResponse;
  year?: string;
  isPreview?: boolean;
  metaDescription?: string;
  eyeCatchImageURL?: string;
};

const QueryParameter = z.object({
  id: z.string(),
  slug: z.string(),
  preview: z.string().optional(),
  preview_code: z.string().optional(),
});

type QueryParameter = z.infer<typeof QueryParameter>;

export const getServerSideProps: GetServerSideProps = withHandler<
  Props,
  'withOptionalSession' | 'withQuery',
  QueryParameter
>(
  {auth: 'optional', queryValidator: QueryParameter},
  async (context): Promise<GetServerSidePropsResult<Props>> => {
    const {id, slug, preview, preview_code} = context.validatedQuery;
    if (!id || !slug) {
      return {
        notFound: true,
      };
    }

    const {cookies} = context.req;
    const {isValid} = validateCookie(cookies);

    // 企業詳細の年度指定ページの場合 (/company/[id]/[year]の場合)
    if (/^\d{4}$/.test(slug)) {
      // 企業詳細の年度指定ページの場合の処理
      const year = slug;
      // プレビューモード対応
      const previewCode = String(context.query.preview_code ?? '');
      const props: Props = {
        year: year ?? '',
        isPreview:
          typeof context.query.preview_code === 'string' &&
          context.query.preview_code !== '',
      };
      // ログイン状態でプレビューコードがある場合はエラー
      if (isValid && previewCode.length > 0) {
        return {
          notFound: true,
        };
      }

      // 未ログイン状態の場合
      if (!isValid) {
        // プレビューモード考慮
        let companyDetailResponse: ResultGetCompanyDetail;
        try {
          companyDetailResponse = await getCompanyDetail({
            companyId: String(id),
            year,
            previewCode,
            webSeminarLimit: 2,
          });
        } catch {
          return {
            notFound: true,
          };
        }

        if (!companyDetailResponse.ok) {
          if (companyDetailResponse.error === NotFoundError) {
            return {
              notFound: true,
            };
          }

          throw new Error(companyDetailResponse.error);
        }

        const idNum = Number(id);
        const company = await getCompanyInfo({
          cookies: context.req.cookies,
          companyId: idNum,
        });
        props.company = company.ok ? company.value : undefined;

        props.companyDetailResponse = companyDetailResponse.ok
          ? companyDetailResponse.value
          : undefined;

        if (!company.ok || !companyDetailResponse.ok) {
          return {
            notFound: true,
          };
        }

        return {
          props,
        };
      }

      // ログイン状態の場合(プレビューコードは考慮しない)
      let companyDetailResponse: ResultGetCompanyDetail;
      try {
        companyDetailResponse = await getCompanyDetail({
          companyId: String(id),
          year,
          previewCode,
          webSeminarLimit: 2,
        });
      } catch {
        return {
          notFound: true,
        };
      }

      if (!companyDetailResponse.ok) {
        return {
          notFound: true,
        };
      }

      const idNum = Number(id);
      const company = await getCompanyInfo({
        cookies: context.req.cookies,
        companyId: idNum,
      });
      props.company = company.ok ? company.value : undefined;

      if (!company.ok || !companyDetailResponse.ok) {
        return {
          notFound: true,
        };
      }

      props.companyDetailResponse = companyDetailResponse.ok
        ? companyDetailResponse.value
        : undefined;

      const userDataResponse = await getUserDataForAnalytics({cookies});
      if (!userDataResponse.ok) {
        return {
          props,
        };
      }

      return {
        props: {
          ...props,
          userDataForAnalytics: userDataResponse.value,
        },
      };
    }

    // 企業配下ページの場合 (/company/[id]/[slug]の場合)
    const redirectToPreviewPage = await handlePostRedirectToPreviewPage({
      cookies,
      wpPreviewParam: preview ?? '',
      postPageType: PostPageType.COMPANY_PAGE,
      companyId: Number.isNaN(Number(id)) ? undefined : Number(id),
      slug,
    });
    if (redirectToPreviewPage !== undefined) {
      return redirectToPreviewPage;
    }

    const getSpecialPostResponse = await getCompanyPagePost({
      companyId: Number(id),
      cookies,
      slug,
      previewCode: preview_code ?? undefined,
    });

    if (!getSpecialPostResponse.ok) {
      if (getSpecialPostResponse.error === NotFoundError) {
        return {
          notFound: true,
        };
      }

      throw new Error(getSpecialPostResponse.error);
    }

    const {parsedContent, metaDescription, isWpContent, eyeCatchImageURL} =
      await handleParsedContent(
        getSpecialPostResponse.value.post.content,
        getSpecialPostResponse.value.post.contentHTML,
        getSpecialPostResponse.value.post.description,
      );

    let props: Props = {
      post: getSpecialPostResponse.value.post,
      parsedContent,
      isWpContent,
      metaDescription,
      eyeCatchImageURL,
    };

    const {companyPages, sponsorCompanies} = getSpecialPostResponse.value.post;
    const isCompanyStudyPost = companyPages && companyPages?.length > 0;
    const isSponsoredPost = sponsorCompanies && sponsorCompanies?.length > 0;
    if (isCompanyStudyPost || isSponsoredPost) {
      const companyId = isSponsoredPost
        ? sponsorCompanies?.[0]?.id
        : companyPages?.[0]?.companyID;

      if (companyId === undefined) {
        throw new UnexpectedError('companyId is undefined');
      }

      const getCompanyInfoResponse = await getCompanyInfo({
        cookies: context.req.cookies,
        companyId,
      });
      if (!getCompanyInfoResponse.ok) {
        throw new Error(getCompanyInfoResponse.error);
      }

      const getRecruitmentListResponse = await getRecruitmentList({
        cookies,
        companyIDs: [companyId],
      });
      if (!getRecruitmentListResponse.ok) {
        throw new Error(getRecruitmentListResponse.error);
      }

      props = {
        ...props,
        companyInfo: getCompanyInfoResponse.value,
        recruitmentList: getRecruitmentListResponse.value,
      };
    }

    if (!isValid) return {props};

    const userDataResponse = await getUserDataForAnalytics({cookies});
    if (!userDataResponse.ok) {
      return {
        props,
      };
    }

    return {
      props: {
        ...props,
        userDataForAnalytics: userDataResponse.value,
      },
    };
  },
);

const Page: NextPageWithLayout<Props> = ({
  userDataForAnalytics,
  post,
  parsedContent,
  companyInfo,
  recruitmentList,
  companyDetailResponse,
  company,
  year,
  isPreview,
  metaDescription,
  eyeCatchImageURL,
}: Props) => {
  const router = useRouter();
  const {id, slug} = router.query;
  const currentUrl = `${GSSKT_HOST}/company/${id}`;
  const isCompanyDetailYear =
    typeof slug === 'string' &&
    /^\d{4}$/.test(slug) &&
    companyDetailResponse !== undefined &&
    company !== undefined;
  const isArchivePage = post !== undefined && parsedContent !== undefined;

  return (
    <>
      {isCompanyDetailYear && (
        <>
          <Head>
            <title key="title">{`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活情報・採用大学 | 外資就活ドットコム`}</title>
            <link rel="canonical" href={currentUrl} key="canonical" />
            <meta
              key="description"
              name="description"
              content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報です。${companyDetailResponse?.metaData?.companyName}のエントリーシートやES・体験記、就活説明会・選考会、企業紹介を掲載。トップ企業の内定をめざす就職活動サイト「外資就活ドットコム」`}
            />
            <meta
              key="keywords"
              name="keywords"
              content={`${companyDetailResponse?.metaData?.companyName},${companyDetailResponse?.metaData?.industryCategoryID},${companyDetailResponse?.metaData?.industryName}外資,就活,インターンシップ,新卒採用,会社説明会,就活コラム,インターン,内定,内定大学,掲示板,体験記,採用,ES・体験記${year ? `,${year}年卒` : ''}`}
            />
            <meta
              key="og:title"
              property="og:title"
              content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報 | 外資就活ドットコム`}
            />
            <meta
              key="og:image"
              property="og:image"
              content={`https://gaishishukatsu.com/sns_image/company/${companyDetailResponse?.metaData?.companyID}`}
            />
            <meta
              key="og:url"
              property="og:url"
              content={`https://gaishishukatsu.com/company/${companyDetailResponse?.metaData?.companyID}`}
            />
            <meta
              key="og:description"
              property="og:description"
              content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報。${companyDetailResponse?.metaData?.companyName}のエントリーシートやES・体験記、就活説明会・選考会、企業紹介を掲載。トップ企業の内定をめざす就職活動サイト「外資就活ドットコム」`}
            />
            <meta
              key="twitter:title"
              name="twitter:title"
              content={`${companyDetailResponse?.metaData?.companyName}の新卒採用・就活・インターンシップ情報 | 外資就活ドットコム`}
            />
            <meta
              key="twitter:description"
              name="twitter:description"
              content={`${companyDetailResponse?.metaData?.companyName}の${year?.length === 0 ? '' : `${year}年卒向け`}新卒採用・就活・インターンシップ情報。${companyDetailResponse?.metaData?.companyName}のエントリーシートやES・体験記、就活説明会・選考会、企業紹介を掲載。トップ企業の内定をめざす就職活動サイト「外資就活ドットコム」`}
            />
            <meta
              key="twitter:image"
              name="twitter:image"
              content={`https://gaishishukatsu.com/sns_image/company/${companyDetailResponse?.metaData?.companyID}`}
            />
            <meta name="robots" content="index, follow" />
          </Head>
          <CompanyDetailPage
            companyDetail={companyDetailResponse}
            companyInfo={company}
            year={year}
            isPreview={isPreview}
          />
        </>
      )}
      {isArchivePage && (
        <>
          <Head>
            <title key="title">{`${post.title}${TITLE_SUFFIX}`}</title>
            {metaDescription && (
              <>
                <meta
                  key="description"
                  name="description"
                  content={metaDescription}
                />
                <meta
                  key="og:description"
                  property="og:description"
                  content={metaDescription}
                />
                <meta
                  key="twitter:description"
                  name="twitter:description"
                  content={metaDescription}
                />
              </>
            )}
            <meta
              key="og:title"
              property="og:title"
              content={`${post.title}${TITLE_SUFFIX}`}
            />
            <meta key="og:url" property="og:url" content={currentUrl} />
            <meta
              key="og:image"
              property="og:image"
              content={post.ogpImageURL}
            />
            <meta
              key="twitter:title"
              name="twitter:title"
              content={`${post.title}${TITLE_SUFFIX}`}
            />
            <meta
              key="twitter:image"
              name="twitter:image"
              content={post.ogpImageURL}
            />
            <link rel="canonical" href={currentUrl} key="canonical" />
            <script type="application/ld+json">
              {JSON.stringify(
                createArticleDetailStructuredData(post, metaDescription),
              )}
            </script>
          </Head>
          <ArchivePage
            post={post}
            parsedContent={parsedContent}
            companyInfo={companyInfo}
            recruitmentList={recruitmentList}
            metaDescription={metaDescription}
            eyeCatchImageURL={eyeCatchImageURL}
          />
        </>
      )}
      <ScriptForGA userDataForAnalytics={userDataForAnalytics} />
    </>
  );
};

export default Page;

Page.getLayout = (page) => {
  const router = useRouter();
  const {slug} = router.query;
  if (typeof slug === 'string' && /^\d{4}$/.test(slug)) {
    return <NoLeftSidebarLayout>{page}</NoLeftSidebarLayout>;
  }

  return <ColumnLayout>{page}</ColumnLayout>;
};
